.root {
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
}

/* Header */
.header {
  position: sticky;
  top: 0;
  z-index: 100;
  background: rgba(246, 243, 238, 0.9);
  border-bottom: 1px solid var(--border);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.headerInner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: var(--header-h);
  width: min(100%, var(--max-w));
  margin: 0 auto;
  padding: 8px clamp(16px, 3vw, 40px);
  flex-wrap: wrap;
}

.profiles {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex-wrap: wrap;
}

.chip {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.chipText {
  min-width: 0;
}

.chipName {
  display: block;
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chipHandle,
.chipState,
.chipError {
  display: block;
  font-size: 0.82rem;
  color: var(--accent, var(--muted));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chipState { color: var(--muted); }
.chipError { color: var(--down); }

/* Only on the compared profiles: the first one is the profile the comparison
   was opened from, and leaving it is what "← Back" does. */
.chipRemove {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  line-height: 1;
  font-size: 1rem;
  color: var(--muted);
  border-radius: 4px;
  transition: color 0.12s, background 0.12s;
}

.chipRemove:hover {
  color: var(--text);
  background: var(--surface-2);
}

.addOpen {
  padding: 6px 12px;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--muted);
  border: 1px dashed var(--border-md);
  border-radius: var(--radius-sm);
  transition: border-color 0.12s, color 0.12s;
}

.addOpen:hover {
  border-color: var(--amber-border);
  color: var(--text);
}

.addForm {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.addInput {
  width: 140px;
  padding: 6px 10px;
  font-size: 0.88rem;
  color: var(--text);
  background: var(--canvas);
  border: 1px solid var(--border-md);
  border-radius: var(--radius-sm);
}

.addInput:focus {
  outline: none;
  border-color: var(--amber-border);
}

.addSubmit {
  padding: 6px 12px;
  font-size: 0.85rem;
  font-weight: 700;
  color: #262119;
  background: var(--amber);
  border-radius: var(--radius-sm);
}

.addSubmit:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.addCancel {
  width: 22px;
  height: 22px;
  line-height: 1;
  font-size: 1rem;
  color: var(--muted);
  border-radius: 4px;
}

.addCancel:hover {
  color: var(--text);
}

.addError {
  font-size: 0.82rem;
  color: var(--down);
}

.vs {
  font-family: var(--font-display);
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--muted);
  padding: 0 4px;
}

.dateRange {
  font-size: 0.85rem;
  color: var(--muted);
  white-space: nowrap;
  padding-left: 8px;
  border-left: 1px solid var(--border-md);
}

.controls {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.periodGroup {
  display: flex;
  gap: 2px;
  padding: 3px;
  background: var(--surface-2);
  border-radius: 9px;
  flex-shrink: 0;
}

/* min-width + flex-basis 0 keeps all four the same width regardless of label
   length ("7d" vs "90d" vs "1y"), so the group reads as a segmented control
   rather than four differently sized chips. */
.periodBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 0;
  min-width: 52px;
  min-height: 32px;
  padding: 4px 8px;
  border-radius: 7px;
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--soft);
  transition: background 0.12s, color 0.12s;
}

.periodBtn:hover:not(.active) {
  color: var(--text);
  background: var(--tint-md);
}

/* No hover and no pointer: clicking the selected segment does nothing, so the
   cursor should not promise otherwise. */
.periodBtn.active {
  background: var(--amber-strong);
  color: #FFFFFF;
  cursor: default;
}

.exitBtn {
  padding: 7px 14px;
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--soft);
  border: 1px solid var(--border-md);
  border-radius: var(--radius-sm);
  transition: border-color 0.12s, color 0.12s, background 0.12s;
}

.exitBtn:hover {
  border-color: var(--amber-border);
  background: var(--amber-dim);
  color: var(--text);
}

.newSearch {
  padding: 7px 14px;
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--muted);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  transition: border-color 0.12s, color 0.12s, background 0.12s;
}

.newSearch:hover {
  border-color: var(--border-md);
  background: var(--surface-2);
  color: var(--text);
}

/* Main content */
.main {
  width: min(100%, var(--max-w));
  margin: 0 auto;
  padding: clamp(20px, 3vw, 40px) clamp(16px, 3vw, 40px) 60px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Each section rises in on load, matching the card/chart animations. Note:
   `animation-fill-mode: both` keeps a transform applied after the run — even
   the final `transform: none` computes to an identity matrix — so these
   sections stay a containing block for position:fixed descendants. Chart
   tooltips sidestep that by portalling to <body> (see ChartTooltip.jsx). */
@keyframes sectionIn {
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: none; }
}

.main > * {
  animation: sectionIn 0.45s ease both;
}

.main > *:nth-child(2) { animation-delay: 70ms; }
.main > *:nth-child(3) { animation-delay: 140ms; }
.main > *:nth-child(4) { animation-delay: 210ms; }
.main > *:nth-child(5) { animation-delay: 280ms; }

@media (prefers-reduced-motion: reduce) {
  .main > * { animation: none; }
}

/* Side-by-side panels */
.splitRow {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  align-items: start;
}

/* Past two profiles each row inside these panels carries one bar per athlete,
   so half a screen stops being enough — they take the full width instead. */
.splitStacked {
  grid-template-columns: minmax(0, 1fr);
}

@media (max-width: 900px) {
  .splitRow {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 640px) {
  .headerInner {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    padding-top: 12px;
    padding-bottom: 12px;
  }

  /* Full-width row with its controls centered as a group. */
  .controls {
    justify-content: center;
    flex-wrap: wrap;
    width: 100%;
    gap: 6px;
  }

  /* 1fr · auto · 1fr grid so "vs" sits at the exact centre of the row no
     matter how wide each athlete's name or avatar is — the two chips hug the
     middle from either side. */
  .profiles {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    width: 100%;
    gap: 8px;
  }

  .profiles > .chip:first-of-type {
    justify-self: end;
  }

  .profiles > .chip:last-of-type {
    justify-self: start;
  }

  /* The add control is not part of the pair, so it gets its own centred row
     under them rather than a grid cell beside a chip. */
  .profiles > .addOpen,
  .profiles > .addForm {
    grid-column: 1 / -1;
    justify-self: center;
  }

  /* Three or four chips do not fit a fixed three-column grid; they wrap. */
  .profilesMany {
    display: flex;
    justify-content: center;
  }

  .chipText {
    text-align: center;
  }

  .dateRange {
    display: none;
  }

  /* The period group takes whatever Back + New Search leave over, and its four
     buttons split that evenly — wider, evenly sized touch targets instead of
     four chips sized by their label. */
  .periodGroup {
    flex: 1 1 auto;
    min-width: 0;
  }

  .periodBtn {
    min-width: 0;
    min-height: 34px;
    padding: 4px 6px;
  }

  /* Sized to the same touch band as the period group so the control row is one
     consistent height. */
  .exitBtn,
  .newSearch {
    display: inline-flex;
    align-items: center;
    min-height: 36px;
    padding: 5px 10px;
    font-size: 0.82rem;
  }
}

/* Default: show the full "New Search" label. */
.newSearchCompact { display: none; }

@media (max-width: 360px) {
  /* Below this, period buttons + Back already reach the row's edge, so
     New Search only fits on the same row with a shorter label. */
  .newSearchCompact { display: inline; }
  .newSearchFull { display: none; }
}
